import { type RefObject, useEffect, useMemo, useRef } from 'react';
import styles from './TopoBackground.module.css';
import { buildTopoContours, TOPO_VIEWBOX, warpContour } from './topoContours';

// Cursor-warp feel: peak displacement (viewBox units), cursor follow lag, and
// an underdamped amplitude spring so the lines overshoot slightly and resettle
// like a disturbed water surface.
const WARP_AMP = 18;
const WARP_FOLLOW = 0.05;
const WARP_STIFF = 0.03;
const WARP_DAMP = 0.9;

type Props = {
  /**
   * The positioned ancestor this paints into, and the element whose pointer moves warp
   * the lines. Passed in rather than owned, because the contours have to react to the
   * cursor anywhere over the screen while the SVG itself stays `pointer-events: none`
   * behind the content.
   */
  containerRef: RefObject<HTMLElement | null>;
};

/**
 * The ambient background shared by the welcome and sign-in screens: a soft blue bloom
 * with procedural topographic contours over it, which yield around the pointer and
 * spring back.
 *
 * One component rather than two copies. The rAF loop below writes path `d` attributes
 * directly instead of going through React state, and parks itself once everything
 * settles, so a second copy of it would be a second animation loop as well as a second
 * place to fix.
 */
export default function TopoBackground({ containerRef }: Props) {
  // Fresh seed per mount so the contour landform differs each visit.
  const contours = useMemo(() => buildTopoContours(Math.random() * Math.PI * 2), []);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const pathRefs = useRef<(SVGPathElement | null)[]>([]);

  // Contours near the pointer yield and spring back. The loop only runs while the warp
  // is moving.
  useEffect(() => {
    const view = containerRef.current;
    const svg = svgRef.current;
    if (!view || !svg) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const target = { x: 0, y: 0, amp: 0 };
    const cur = { x: 0, y: 0, amp: 0, vel: 0 };
    const displaced = contours.map(() => false);
    let raf = 0;
    let running = false;

    const tick = () => {
      cur.x += (target.x - cur.x) * WARP_FOLLOW;
      cur.y += (target.y - cur.y) * WARP_FOLLOW;
      cur.vel = (cur.vel + (target.amp - cur.amp) * WARP_STIFF) * WARP_DAMP;
      cur.amp += cur.vel;

      contours.forEach((c, i) => {
        const node = pathRefs.current[i];
        if (!node) return;
        const d = Math.abs(cur.amp) < 0.05 ? null : warpContour(c.pts, cur.x, cur.y, cur.amp);
        if (d) {
          node.setAttribute('d', d);
          displaced[i] = true;
        } else if (displaced[i]) {
          node.setAttribute('d', c.d);
          displaced[i] = false;
        }
      });

      const settled =
        Math.abs(target.amp - cur.amp) < 0.02 &&
        Math.abs(cur.vel) < 0.02 &&
        Math.abs(target.x - cur.x) < 0.5 &&
        Math.abs(target.y - cur.y) < 0.5;
      if (settled) {
        running = false;
        return;
      }
      raf = requestAnimationFrame(tick);
    };

    const start = () => {
      if (!running) {
        running = true;
        raf = requestAnimationFrame(tick);
      }
    };

    const onMove = (e: PointerEvent) => {
      const m = svg.getScreenCTM();
      if (!m) return;
      const p = new DOMPoint(e.clientX, e.clientY).matrixTransform(m.inverse());
      // Snap to the pointer when the warp is at rest, so it doesn't sweep in
      // from wherever the cursor last left off.
      if (target.amp === 0 && Math.abs(cur.amp) < 0.05) {
        cur.x = p.x;
        cur.y = p.y;
      }
      target.x = p.x;
      target.y = p.y;
      target.amp = WARP_AMP;
      start();
    };
    const onLeave = () => {
      target.amp = 0;
      start();
    };

    view.addEventListener('pointermove', onMove);
    view.addEventListener('pointerleave', onLeave);
    return () => {
      view.removeEventListener('pointermove', onMove);
      view.removeEventListener('pointerleave', onLeave);
      cancelAnimationFrame(raf);
    };
  }, [contours, containerRef]);

  return (
    <>
      <div className={`${styles.bloom} ${styles.bloomCenter}`} aria-hidden="true" />
      <svg
        ref={svgRef}
        className={styles.topo}
        viewBox={`0 0 ${TOPO_VIEWBOX.w} ${TOPO_VIEWBOX.h}`}
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
      >
        <defs>
          <radialGradient
            id="topoStroke"
            gradientUnits="userSpaceOnUse"
            cx={TOPO_VIEWBOX.w / 2}
            cy={TOPO_VIEWBOX.h / 2}
            r={740}
          >
            <stop offset="0%" stopColor="#0098DB" />
            <stop offset="45%" stopColor="#1FAACF" />
            <stop offset="100%" stopColor="#63CECA" />
          </radialGradient>
        </defs>
        <g fill="none" stroke="url(#topoStroke)" strokeLinejoin="round">
          {contours.map((c, i) => (
            <path
              key={c.id}
              ref={(el) => {
                pathRefs.current[i] = el;
              }}
              d={c.d}
              strokeWidth={c.w}
              opacity={c.o}
            />
          ))}
        </g>
      </svg>
    </>
  );
}
